import { ReactNode, Suspense, useMemo } from 'react'
import { Outlet } from '@tanstack/react-router'
import { AppShell, Text, Group, Title, useMantineColorScheme, Box } from '@mantine/core'
import { CenteredLoader } from '@/components/CenteredLoader'
import { Navigation, navigationTabs } from '@/components/Navigation'
import { useNavigationCollapse } from '@/hooks/useNavigationCollapse'
import NavigationToggle from './NavigationToggle'
import { useAuthStore } from '@/stores/auth'
import { hasAnyPermission } from '@/utils/permissions'
import { AccessCategory } from '@/api/main/types.gen'
import MobileNavigation from './MobileNavigation'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { HOTLINE_NUMBER } from '@/constants'
import RippleIcon from '../RippleIcon'
import { CITY_NAME } from '@/meta'
import LanguagePicker from './LanguagePicker'
import ThemeToggle from '../ThemeToggle'
import { MockModePill } from '../Dev/MockModePill'

const MainLayout = ({
  collapsed: implicitlyCollapsed = false,
  children,
  secondHeader,
}: {
  collapsed?: boolean
  navigation?: ReactNode
  children?: ReactNode
  secondHeader?: ReactNode
}) => {
  const { collapsed } = useNavigationCollapse({
    defaultCollapsed: implicitlyCollapsed,
  })

  const { t } = useTranslation('nav')
  const { colorScheme } = useMantineColorScheme()

  const navbarWidth = collapsed ? 0 : 300

  const isMobile = useMediaQuery('(max-width: 768px)')

  const { userPermissions } = useAuthStore()

  // Filter tabs based on user permissions
  const filteredTabs = useMemo(() => {
    const filtered = { ...navigationTabs }

    // Filter system tabs
    filtered.system = navigationTabs.system.filter(
      item => !item.permission || hasAnyPermission(userPermissions, item.permission as AccessCategory)
    )

    // Filter users tabs
    filtered.users = navigationTabs.users.filter(
      item => !item.permission || hasAnyPermission(userPermissions, item.permission as AccessCategory)
    )

    return filtered
  }, [userPermissions])

  const isDark = colorScheme === 'dark'
  return (
    <>
      <AppShell
        header={{
          height: 60,
        }}
        navbar={{
          width: { base: navbarWidth },
          breakpoint: 'sm',
          collapsed: { mobile: true },
        }}
        styles={{
          main: {
            background: isDark ? 'var(--app-background-dark)' : 'var(--app-background)',
          },
        }}
        padding="md"
      >
        <AppShell.Header>
          <Group align="center" h="100%" gap="xs">
            <Group
              justify="space-between"
              align="center"
              h="100%"
              style={{
                width: 300,
                borderRight: '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
              }}
              pr="xs"
              pl="md"
            >
              <NavigationToggle />
              <Title fz="h" c="light-dark(#868e96, #c9c9c9)" order={2} style={{ margin: 0, lineHeight: 1 }}>
                {t('citySense')}
              </Title>
            </Group>
            <Text
              size="md"
              c="light-dark(#868e96, #c9c9c9)"
              style={{
                marginTop: 0,
                marginBottom: 0,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {CITY_NAME} &nbsp;
            </Text>
            <Group gap="md" ml="auto" pr="md" align="center">
              <Box display="inline-flex" style={{ alignItems: 'center' }}>
                <RippleIcon color="var(--mantine-color-green-7)" />
                <Text size="md" c="light-dark(#868e96, #c9c9c9)" pl="xs" fw={600}>
                  {HOTLINE_NUMBER}
                </Text>
              </Box>
              <ThemeToggle />
              <LanguagePicker />
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Main>
          {secondHeader}
          <Suspense fallback={<CenteredLoader />}>{children || <Outlet />}</Suspense>
        </AppShell.Main>

        <AppShell.Navbar display={collapsed ? 'none' : undefined}>
          <Navigation />
        </AppShell.Navbar>
      </AppShell>
      <MobileNavigation system={filteredTabs.system} users={filteredTabs.users} render={isMobile} />
      {import.meta.env.DEV ? <MockModePill /> : null}
    </>
  )
}

export default MainLayout
